import { useTranslation } from 'react-i18next';
import { Info, Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EntityMultiSelect } from '~/components/promotion/entity-multi-select';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { loadMockProducts } from '~/lib/lookups/mock';
import { cn } from '@/lib/utils';
import type { PromotionAction } from '~/lib/schemas/actions';
import { DiscountModeInput } from './discount-mode-input';

type Action = Extract<PromotionAction, { type: 'per-product-discount' }>;
type Row = Action['rows'][number];

interface Props {
	value: Action;
	onChange: (next: Action) => void;
}

const COLS = 'grid-cols-[1fr_70px_90px_72px_70px_32px]';

/**
 * Constraint #4: maxQty (ΜΕΓ.) is a hard GATING cap — if the cart holds MORE than
 * maxQty of the matched product the whole policy is gated off. The column-header
 * tooltip surfaces this; the Zod refinement enforces maxQty ≥ minQty. Empty = unlimited.
 */
export function PerProductDiscountBody({ value, onChange }: Props) {
	const { t } = useTranslation();
	const setRow = (index: number, patch: Partial<Row>) =>
		onChange({ ...value, rows: value.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
	const removeRow = (index: number) => onChange({ ...value, rows: value.rows.filter((_, i) => i !== index) });
	const addRow = () =>
		onChange({ ...value, rows: [...value.rows, { productId: 0, minQty: 1, discount: 0, discountMode: 'percentage', maxQty: null }] });

	return (
		<TooltipProvider>
			<div className="flex flex-col gap-2">
				<div className={cn('grid items-center gap-2 px-1', COLS)}>
					<span />
					<span className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
						{toGreekCaps(t('promotion.action.perProduct.minQty'))}
					</span>
					<span className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
						{toGreekCaps(t('promotion.action.perProduct.discount'))}
					</span>
					<span className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
						{toGreekCaps(t('promotion.action.discountMode.label'))}
					</span>
					<span className="flex items-center gap-1 text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
						{toGreekCaps(t('promotion.action.perProduct.maxQty'))}
						<Tooltip>
							<TooltipTrigger asChild>
								<button type="button" className="text-foreground-subtle" aria-label="info">
									<Info size={11} />
								</button>
							</TooltipTrigger>
							<TooltipContent className="max-w-56">{t('promotion.action.perProduct.maxQtyTooltip')}</TooltipContent>
						</Tooltip>
					</span>
					<span />
				</div>

				{value.rows.map((row, index) => (
					<div key={index} className={cn('grid items-center gap-2', COLS)}>
						<EntityMultiSelect
							value={row.productId ? [row.productId] : []}
							onChange={ids => setRow(index, { productId: ids[ids.length - 1] ?? 0 })}
							options={[]}
							loadOptions={loadMockProducts}
							placeholder={t('common.search')}
						/>
						<Input type="number" min={1} value={row.minQty} onChange={e => setRow(index, { minQty: Number(e.target.value) || 0 })} />
						<Input
							type="number"
							min={0}
							max={row.discountMode === 'percentage' ? 100 : undefined}
							value={row.discount || ''}
							onChange={e => setRow(index, { discount: Number(e.target.value) || 0 })}
						/>
						<DiscountModeInput value={row.discountMode} onChange={discountMode => setRow(index, { discountMode })} />
						<Input
							type="number"
							min={1}
							placeholder="∞"
							value={row.maxQty ?? ''}
							onChange={e => setRow(index, { maxQty: e.target.value === '' ? null : Number(e.target.value) || null })}
						/>
						<button
							type="button"
							onClick={() => removeRow(index)}
							className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted hover:bg-muted hover:text-foreground"
							aria-label="remove row">
							<X size={14} />
						</button>
					</div>
				))}

				<button
					type="button"
					onClick={addRow}
					className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-sm text-foreground-muted transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg">
					<Plus size={14} /> {t('common.add')}
				</button>
			</div>
		</TooltipProvider>
	);
}
